import { useQuery } from "@tanstack/react-query";
import type { SubscriptionResponse } from "../repositories/subscription.repository";
import { subscriptionRepository } from "../repositories/subscription.repository";
import { shouldRetryQuery } from "../utils/queryRetry";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";

/**
 * Query key helper for current subscription
 * Supports both terreiro and medium (personal) contexts
 */
export const currentSubscriptionQueryKey = (context?: {
  terreiroId?: string;
  mediumId?: string;
}) => {
  const id = context?.terreiroId ?? context?.mediumId ?? "no-context";
  const type = context?.terreiroId
    ? "terreiro"
    : context?.mediumId
      ? "medium"
      : "unknown";
  return ["subscription", type, id, "current"] as const;
};

interface UseCurrentSubscriptionQueryProps {
  /** Override the terreiro ID (defaults to cached terreiro) */
  terreiroId?: string;
  /** Override the medium ID (defaults to cached medium) - for personal subscriptions */
  mediumId?: string;
  /** Enable/disable the query */
  enabled?: boolean;
}

/**
 * Hook to fetch current subscription for terreiro or personal (medium) context
 *
 * Automatically detects context from cached data if not explicitly provided.
 * Supports both terreiro subscriptions and personal subscriptions for mediums.
 *
 * @example
 * ```tsx
 * // Auto-detect context
 * const { data: subscription } = useCurrentSubscriptionQuery();
 *
 * // Explicit terreiro context
 * const { data: subscription } = useCurrentSubscriptionQuery({ terreiroId: 'terreiro-123' });
 *
 * // Explicit medium context (personal subscription)
 * const { data: subscription } = useCurrentSubscriptionQuery({ mediumId: 'medium-456' });
 * ```
 */
export function useCurrentSubscriptionQuery(
  props: UseCurrentSubscriptionQueryProps = {},
) {
  const {
    terreiroId: overrideTerreiroId,
    mediumId: overrideMediumId,
    enabled = true,
  } = props;
  const cachedTerreiro = useCachedTerreiro();
  const cachedMedium = useCachedMedium();

  const callerManaged =
    Object.prototype.hasOwnProperty.call(props, "terreiroId") ||
    Object.prototype.hasOwnProperty.call(props, "mediumId");

  // When caller provides explicit overrides, use ONLY those (don't mix with stale cached values).
  // This prevents race conditions when SubscriptionContext passes atomic context.
  const terreiroId = callerManaged ? overrideTerreiroId : cachedTerreiro?.id;
  const mediumId = callerManaged ? overrideMediumId : cachedMedium?.id;

  // Determine personal profile mode:
  // - Caller-managed: personal if no terreiroId provided
  // - Self-managed: personal if cachedTerreiro is explicitly null
  const isPersonalProfile = callerManaged
    ? !overrideTerreiroId
    : cachedTerreiro === null;

  // Prioritize terreiro context if both exist, BUT respect personal profile mode
  const hasTerreiro = !isPersonalProfile && !!terreiroId;
  const hasMedium = (isPersonalProfile || !hasTerreiro) && !!mediumId;

  // Build context object
  const context = hasTerreiro
    ? { terreiroId }
    : hasMedium
      ? { mediumId }
      : undefined;

  // Enable query only when we have a valid context
  const shouldEnableQuery = enabled && !!context;

  return useQuery<SubscriptionResponse | null, unknown>({
    queryKey: currentSubscriptionQueryKey(context),
    queryFn: () => {
      // Backend API will determine subscription based on context
      return subscriptionRepository.getCurrentSubscription(
        hasTerreiro ? terreiroId : undefined,
        hasMedium ? mediumId : undefined,
      );
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnMount: "always",
    enabled: shouldEnableQuery,

    retry: shouldRetryQuery,

    retryDelay: (attemptIndex) => {
      // Exponential backoff: 1s, 2s, 4s
      return Math.min(1000 * 2 ** attemptIndex, 4000);
    },

    // Refetch on window focus for fresh data
    refetchOnWindowFocus: true,

    // Refetch every 5 minutes when window is visible
    refetchInterval: 1000 * 60 * 5,

    // Fail silently on errors (return null instead of throwing)
    // This prevents blocking the app on subscription check failures
    meta: {
      errorMessage: "Falha ao carregar status da assinatura",
    },
  });
}
